import { ethers } from 'ethers'
import { getAddressFromModal } from './Wallet-connect.js'
/** @type {*} */
// TODO: do we need B_CHAIN_NETWORK or this env/network variable anymore?
const env = process.env.B_CHAIN_NETWORK

/** @type {'testnet'|'dev'} */
const network = env

import AppEvents from '../src/utilities/app-events.js'
import ABIS from '../src/data/services/contractabis.js'
import {
  DaoRoles,
  KipRoles,
  updateSupportedDaos,
  updateSupportedKips,
} from './models/daos.values.js'
import { getUserKipRoles } from '../src/data/services/kip.service.js'
import {
  getSessionGlobals,
  storeSessionGlobals,
} from '../src/utilities/session.js'
import { getRegionalDaoOwners } from '../src/data/services/proposals.service.js'
import { addressesEqual } from '../src/utilities/crypto.js'

/**
 * @type {Object<string, ConnectionStatusOptions>} ConnectionStatusOpts
 */
export const ConnectionStatus = {
  CONNECTED: 'connected',
  CONNECTING: 'connecting',
  DISCONNECTED: 'disconnected',
  FAILED: 'failed',
}

/**
 * @class Globals
 * @classdesc A class for managing global variables and settings
 * @property {Object<string, DaoSettings>} daos - A list of whitelisted regional dao addresses
 * @property {WalletAccount} selectedAccount - The currently selected account
 * @property {DaoSettings} selectedDao - The currently selected dao
 * @property {(e: CustomEvent) => void} onUpdate - Adds an event listener for global updates
 * @property {(settings: SessionAccountSettings) => void} update
 *  - Updates the selected account and dispatches a global update event
 *  @param {SessionAccountSettings | null} init - The initial settings for the class
 */
export default class Globals {
  /** @type {Object<string, DaoSettings>} */
  #regionalDaos = null
  /** @type {Object<string, KipDaoSettings>} */
  #kip = null
  #kipRoles = null
  #state = {
    /** @type {'testnet' | 'dev'} */
    network: null,
    /** @type {string[]} */
    accounts: null,
    /** @type {WalletAccount} */
    selectedAccount: null,
    /** @type {UserWallet[]} */
    wallets: null,
    /** @type {DaoSettings} */
    selectedDao: null,
    /** @type {ConnectionStatusOptions} */
    status: ConnectionStatus.DISCONNECTED,
  }

  /**
   * @constructor
   * @param {SessionAccountSettings} init - The initial settings for the class
   */
  constructor(init = getSessionGlobals()) {
    this.initializationPromise = Promise.all([
      updateSupportedDaos(),
      updateSupportedKips(),
    ]).then(async ([daos, kip]) => {
      this.#regionalDaos = daos
      this.#kip = kip

      this.#kipRoles = null
      this.globalsInitialized = true

      this.update({
        network,
        selectedAccount: init?.selectedAccount || null,
        accounts: init?.accounts || null,
        wallets: init?.wallets || null,
        status: init?.status || ConnectionStatus.DISCONNECTED,
      })

      if (kip && init?.wallets?.[0]?.address) {
        try {
          const governorAddress = kip?.governorAddress
          if (governorAddress) {
            const roles = await getUserKipRoles(
              governorAddress,
              init.wallets[0].address
            )
            this.#kipRoles = roles
            this.dispatchUpdate({
              ...this.#state,
            })
          }
        } catch (error) {}
      }
    })
  }

  /**
   * @type {Object<string, DaoSettings>}
   * @readonly
   */
  get daos() {
    return this.#regionalDaos
  }

  /**
   * @type {Object<string, KipDaoSettings>}
   * @readonly
   */
  get kip() {
    return this.#kip
  }

  /**
   * @type {WalletAccount}
   * @readonly
   */
  get selectedAccount() {
    return this.#state.selectedAccount
  }

  /**
   * @type {DaoSettings}
   * @readonly
   */
  get selectedDao() {
    return this.#state.selectedDao
  }

  /**
   * @type {string[]}
   * @readonly
   */
  get accounts() {
    return this.#state.accounts
  }

  /**
   * @type {UserWallet[]}
   * @readonly
   */
  get wallets() {
    return this.#state.wallets
  }

  /**
   * @type {ConnectionStatusOptions}
   * @readonly
   */
  get status() {
    return this.#state.status
  }

  /**
   * @type {Object<string, KipRoles>}
   * @readonly
   */
  get kipRoles() {
    return this.#kipRoles
  }

  /**
   * @function update
   * @description Updates the selected account and dispatches a global update event
   * @param {{
   *  network?: 'testnet' | 'dev',
   *  selectedAccount?: WalletAccount,
   *  accounts?: string[],
   *  status?: ConnectionStatusOptions,
   *  wallets?: UserWallet[],
   * }} settings - The new settings to update
   */
  update({
    network = this.#state?.network,
    selectedAccount = this.#state?.selectedAccount,
    accounts = this.#state?.accounts || [],
    wallets = this.#state?.wallets || [],
    status = this.#state?.status,
  }) {
    if (!this.globalsInitialized) {
      return
    }

    this.#state = {
      network,
      selectedAccount,
      selectedDao: this.#regionalDaos[selectedAccount?.daoAddress] || null,
      accounts,
      status,
      wallets,
    }

    storeSessionGlobals(this.#state)
    this.dispatchUpdate(this.#state)
  }

  /** @param {SessionAccountSettings} detail */
  dispatchUpdate(detail) {
    window.dispatchEvent(
      new CustomEvent(AppEvents.GLOBALS_UPDATED, {
        detail,
      })
    )
  }

  /**
   * @function onUpdate
   * @description Adds an event listener for global updates
   * @param {(e: CustomEvent) => void} callback - The function to be called on update event
   * @returns {Promise<Function>} - Promise that resolves to cleanup function
   */
  async onUpdate(callback) {
    await this.initializationPromise
    window.addEventListener(AppEvents.GLOBALS_UPDATED, callback)

    callback(
      new CustomEvent(AppEvents.GLOBALS_UPDATED, {
        detail: this.#state,
      })
    )

    return () => window.removeEventListener(AppEvents.GLOBALS_UPDATED, callback)
  }

  async initW3(tryIndex = 0) {
    this.update({ status: ConnectionStatus.CONNECTING })
    window.dispatchEvent(new CustomEvent('modal-connect'))

    try {
      const modalAddress = await getAddressFromModal()
      const accounts = modalAddress ? [modalAddress] : []
      const wallets = await this.buildWallets(accounts)

      this.update({
        accounts,
        wallets,
        status: ConnectionStatus.CONNECTED,
      })
    } catch (error) {
      ++tryIndex
      let timeout = tryIndex * 300
      if (tryIndex > 8) {
        this.update({ status: ConnectionStatus.FAILED })
        return
      }
      setTimeout(() => this.initW3(tryIndex), timeout)
    }
  }

  async checkWallets() {
    if (this.#regionalDaos && Object.keys(this.#regionalDaos).length > 0) {
      try {
        const accounts = [await getAddressFromModal()]
        this.update({
          wallets: await this.buildWallets(accounts),
        })
      } catch (error) {
        this.update({ status: ConnectionStatus.DISCONNECTED })
      }
    }
  }

  async deviceCheck() {
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    const isMetamask = /MetaMask/.test(navigator.userAgent)
    if (isMetamask && isMobile) {
      return true
    }
    if (!isMetamask && isMobile) {
      return false
    }
    if (!isMobile) return true
  }

  /**
   * @function buildWallets
   * @param {string[]} walletAddresses
   * @returns {Promise<UserWallet[]>}
   */
  async buildWallets(walletAddresses) {
    const wallets = []
    let i = 1

    for (const walletAddress of walletAddresses) {
      try {
        const wallet = {
          address: walletAddress,
          name: 'My Wallet ' + i,
          accounts: (
            await Promise.all(
              Object.values(window.Globals.daos).map(async (regional) => {
                const accounts = await this.buildAccount(
                  walletAddress,
                  regional
                )
                return accounts
              })
            )
          ).flat(),
        }
        wallets.push(wallet)
        i++
      } catch (error) {
        console.error('Error building wallets: ', error)
      }
    }

    return wallets || []
  }

  /**
   * @function buildAccount
   * @param {string} walletAddress
   * @param {DaoSettings} regionalDao
   * @returns {Promise<WalletAccount[]>}
   */
  async buildAccount(walletAddress, regionalDao) {
    let owners = []

    if (!walletAddress || !regionalDao) {
      return owners
    }

    try {
      owners = await getRegionalDaoOwners(regionalDao)
    } catch (error) {
      console.error('Error building account: ', error)
    }

    /** @type {WalletAccount[]} */
    const accounts = [
      {
        role: DaoRoles.MEMBER,
        daoAddress: regionalDao.address,
        daoName: regionalDao.daoName,
        erc20Address: regionalDao.daoTokenAddress,
        walletAddress,
        balance: await this.getErc20BalanceForWallet(
          walletAddress,
          regionalDao.daoTokenAddress
        ),
      },
    ]

    if (owners.some((address) => addressesEqual(address, walletAddress)))
      accounts.push({
        role: DaoRoles.EXECUTIVE,
        daoAddress: regionalDao.address,
        daoName: regionalDao.daoName,
        erc20Address: regionalDao.daoTokenAddress,
        walletAddress,
        balance: await this.getErc20BalanceForWallet(
          walletAddress,
          regionalDao.daoTokenAddress
        ),
      })

    return accounts
  }

  async buildKipAccounts(governorAddress, walletAddress) {
    let kipRoles = []
    const kipAccounts = []

    try {
      kipRoles = await getUserKipRoles(governorAddress, walletAddress)
    } catch (error) {
      console.error('Error building account: ', error)
    }
    return kipAccounts
  }

  /**
   * @param {string} walletAddress
   * @param {string} erc20Address
   * @returns {Promise<number>}
   */
  async getErc20BalanceForWallet(walletAddress, erc20Address) {
    if (!walletAddress || !erc20Address) {
      return 0
    }

    if (!window.ethereum) {
      return 0
    }

    try {
      // TODO: W3 READ - Uses Infura token via window.ethereum provider
      const provider = new ethers.providers.Web3Provider(window.ethereum)
      const contract = new ethers.Contract(
        erc20Address,
        ABIS.REGIONAL_DAO_TOKEN.methods,
        provider
      )

      // TODO: W3 READ - Uses Infura token via contract.balanceOf call
      const balance = await contract.balanceOf(walletAddress)
      return +balance
    } catch (error) {
      return 0
    }
  }
}
